import {
  ForwardRefExoticComponent,
  RefAttributes,
  SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Flex } from "styled-system/jsx";
import { Button, Icon, Text, useToastContext } from "@ttc3k/trekker";
import {
  Calendar,
  City,
  List,
  MessageText,
  Pin,
  Shop,
  Trash,
} from "iconoir-react";
import { EnumUserQuickLinksType } from "gql/generated";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { DraggableItem, Sortable, WhereaboutsModal } from "shared/components";
import { QuickLink, normalizeEntityId } from "./useQuickLinks";

const iconMap: Record<
  EnumUserQuickLinksType,
  ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >
> = {
  [EnumUserQuickLinksType.Operator]: List,
  [EnumUserQuickLinksType.Organization]: City,
  [EnumUserQuickLinksType.Storefront]: Shop,
  [EnumUserQuickLinksType.Broadcast]: MessageText,
  [EnumUserQuickLinksType.Event]: Calendar,
};

type QuickLinksSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  quickLinks: QuickLink[];
  onSave: (links: QuickLink[]) => Promise<void>;
  /** When false, the delete control is omitted (e.g. required enrolled organization links). */
  canRemoveLink?: (link: QuickLink) => boolean;
};

type DraftLink = QuickLink & { _id: string };

const toDraftLinks = (links: QuickLink[]): DraftLink[] =>
  links.map(ql => {
    const entityId = normalizeEntityId(ql.entityId);
    return {
      ...ql,
      entityId,
      _id: `${ql.type}:${entityId}`,
    };
  });

export const QuickLinksSettingsModal = ({
  isOpen,
  onClose,
  quickLinks,
  onSave,
  canRemoveLink = () => true,
}: QuickLinksSettingsModalProps) => {
  const { t } = useTranslation("core", { keyPrefix: "HOME_PAGE" });
  const { toastFactory } = useToastContext();

  const prevIsOpenRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState<DraftLink[]>([]);
  /** Snapshot when the modal opened — used for dirty check and cancel (not recomputed on every parent quickLinks reference change). */
  const [baselineDraft, setBaselineDraft] = useState<DraftLink[]>([]);

  // Only hydrate draft when the modal opens. Parent `quickLinks` changes often (new array
  // identity each render); syncing on [isOpen, quickLinks] was resetting edits and saving the full list.
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    if (isOpen && !wasOpen) {
      const next = toDraftLinks(quickLinks);
      setDraft(next);
      setBaselineDraft(next);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, quickLinks]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(baselineDraft),
    [draft, baselineDraft],
  );

  const handleTogglePin = useCallback((index: number) => {
    setDraft(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, pinned: !item.pinned } : item,
      ),
    );
  }, []);

  const handleRemove = useCallback((index: number) => {
    setDraft(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSortChange = useCallback(
    async (newItems: Partial<DraftLink>[]) => {
      setDraft(newItems as DraftLink[]);
    },
    [],
  );

  const handleSave = useCallback(async () => {
    const cleaned: QuickLink[] = draft.map(
      ({ _id: _, ...rest }) => rest,
    );
    setIsSaving(true);
    try {
      await onSave(cleaned);
      onClose();
    } catch {
      toastFactory.error({
        title: t("QUICK_LINKS_SAVE_ERROR"),
      });
    } finally {
      setIsSaving(false);
    }
  }, [draft, onSave, onClose, toastFactory, t]);

  const handleCancel = useCallback(() => {
    if (isSaving) return;
    setDraft(baselineDraft);
    onClose();
  }, [baselineDraft, onClose, isSaving]);

  return (
    <WhereaboutsModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t("QUICK_LINKS")}
      size="sm"
      saveButtonProps={{
        onClick: handleSave,
        disabled: !isDirty || isSaving,
        isLoading: isSaving,
        visual: "accent.blue",
      }}
      cancelButtonProps={{
        onClick: handleCancel,
        disabled: isSaving,
      }}
    >
      {draft.length === 0 ? (
        <Text color="text.light" py="300">
          No quick links yet.
        </Text>
      ) : (
        <Sortable
          disabled={false}
          value={draft}
          selectorID="_id"
          sortingStrategy={verticalListSortingStrategy}
          sortableComponent={(item, i) => {
            const link = item as DraftLink;
            return (
              <DraggableItem>
                <Flex
                  gap="200"
                  alignItems="center"
                  width="100%"
                  onPointerDown={e => e.stopPropagation()}
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                >
                  <Flex flex={1} minWidth={0} alignItems="center" gap="150">
                    <Icon
                      Element={iconMap[link.type]}
                      color="icon.highlight.light"
                      style={{ flexShrink: 0 }}
                    />
                    <Text
                      visual="bodyMd"
                      color="text.dark"
                      lineClamp={1}
                    >
                      {link.title}
                    </Text>
                  </Flex>
                  <Flex alignItems="center" gap="100" flexShrink={0}>
                    <Button
                      Icon={Pin}
                      visual="unstyled"
                      type="button"
                      onClick={() => handleTogglePin(i)}
                      color={link.pinned ? "icon.highlight.light" : "icon.light"}
                      style={{
                        padding: 0,
                        minWidth: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                    {canRemoveLink(link) && (
                      <Button
                        Icon={Trash}
                        visual="unstyled"
                        type="button"
                        onClick={() => handleRemove(i)}
                        color="icon.light"
                        style={{
                          padding: 0,
                          minWidth: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    )}
                  </Flex>
                </Flex>
              </DraggableItem>
            );
          }}
          onChange={handleSortChange}
          size="compact"
        />
      )}
    </WhereaboutsModal>
  );
};
